import { RoutePrefetchQueryOptions } from '../routes/types.ts';
import { getInitializedMomentFetcher } from '@moment/net/fetch-contstants.ts';
import {
  DailyWeatherApiResponse,
  LocationApiResponse,
  WeatherApiResponse,
  WeatherV4Response,
} from '@moment/net/accuweather-types.ts';

const trunc = (num: number): string | null => {
  const match = num?.toString().match(/^-?\d+(?:\.\d{0,2})?/);
  if (!match || !match[0]) return null;
  const parts = match[0].split('.');
  const integerPart = parts?.length >= 0 ? parts[0] : '';
  let decimalPart = parts?.length > 0 ? parts[1] : '';
  decimalPart = (decimalPart || '').padEnd(2, '0');
  return `${integerPart}.${decimalPart}`;
};

export const generateLocationQuery = (
  locationApiBaseUrl: string,
  options?: RoutePrefetchQueryOptions
) => {
  return async (latitude: number, longitude: number) => {
    if (
      !latitude ||
      !longitude ||
      Number.isNaN(latitude) ||
      Number.isNaN(longitude)
    ) {
      console.warn(
        `Invalid weather data provided: latitude: ${latitude}, longitude: ${longitude}`
      );
      throw new Error(
        'Invalid geolocation supplied to generateFetchLocationQuery'
      );
    }

    const momentFetcher = await getInitializedMomentFetcher();
    const logger = options?.logger || undefined;

    const truncLong = trunc(longitude);
    const truncLat = trunc(latitude);
    const url = `${locationApiBaseUrl}/api/3.0/location/accuweather/${truncLat}/${truncLong}`;
    const res = await momentFetcher.fetch(url, options);
    logger?.log(
      `generateFetchLocationQuery - hit\n${url}\nstatus=${res.status}`
    );
    const output: LocationApiResponse = await res.json();

    return output satisfies LocationApiResponse;
  };
};

export const generateWeatherQuery = (
  locationApiBaseUrl: string,
  options?: RoutePrefetchQueryOptions
) => {
  return async (locationId: string) => {
    if (!locationId) {
      console.warn(`Invalid location provided: ${locationId}`);
      throw new Error(
        'Invalid locationId supplied to generateFetchWeatherQuery'
      );
    }

    const momentFetcher = await getInitializedMomentFetcher();
    const logger = options?.logger || undefined;

    const url = `${locationApiBaseUrl}/api/3.0/weather/accuweather/current/${locationId}`;
    const res = await momentFetcher.fetch(url, options);
    logger?.log(
      `generateFetchWeatherQuery - hit\n${url}\nstatus=${res.status}`
    );
    const output: WeatherApiResponse = await res.json();

    return output satisfies WeatherApiResponse;
  };
};

export const generateDailyQuery = (
  locationApiBaseUrl: string,
  options?: RoutePrefetchQueryOptions
) => {
  return async (locationId: string) => {
    if (!locationId) {
      console.warn(`Invalid location provided: ${locationId}`);
      throw new Error('Invalid locationId supplied to generateFetchDailyQuery');
    }

    const momentFetcher = await getInitializedMomentFetcher();
    const logger = options?.logger || undefined;

    const url = `${locationApiBaseUrl}/api/3.0/weather/accuweather/daily/${locationId}`;
    const res = await momentFetcher.fetch(url, options);
    logger?.log(
      `generateWeatherDailyQuery - hit\n${url}\nstatus=${res.status}`
    );
    const output: DailyWeatherApiResponse = await res.json();

    return output satisfies DailyWeatherApiResponse;
  };
};

export const generateWeatherComboQuery = (
  locationApiBaseUrl: string,
  options?: RoutePrefetchQueryOptions
) => {
  return async (latitude: number, longitude: number) => {
    if (
      !latitude ||
      !longitude ||
      Number.isNaN(latitude) ||
      Number.isNaN(longitude)
    ) {
      console.warn(
        `Invalid weather data provided, latitude: ${latitude}, longitude: ${longitude}`
      );
      throw new Error(
        'Invalid geolocation supplied to generateWeatherComboQuery'
      );
    }

    const momentFetcher = await getInitializedMomentFetcher();
    const logger = options?.logger || undefined;

    const truncLong = trunc(longitude);
    const truncLat = trunc(latitude);
    const url = `${locationApiBaseUrl}/api/3.0/combo/accuweather/latlong/${truncLat}/${truncLong}`;
    const res = await momentFetcher.fetch(url, options);
    logger?.log(
      `generateWeatherComboQuery - hit\n${url}\nstatus=${res.status}`
    );
    const output: WeatherV4Response = await res.json();

    return output satisfies WeatherV4Response;
  };
};

export const generateWeatherComboNoGeoDataQuery = (
  locationApiBaseUrl: string,
  options?: RoutePrefetchQueryOptions
) => {
  return async () => {
    console.warn(
      `No weather data provided, relying on fallback IP reverse lookup query`
    );

    const momentFetcher = await getInitializedMomentFetcher();
    const logger = options?.logger || undefined;

    const url = `${locationApiBaseUrl}/api/3.0/combo/accuweather`;
    const res = await momentFetcher.fetch(url, options);
    logger?.log(
      `generateWeatherComboQueryNoGeoData - hit\n${url}\nstatus=${res.status}`
    );
    const output: WeatherV4Response = await res.json();

    return output satisfies WeatherV4Response;
  };
};
